/**
 * @license
 * Copyright Mauricio Gemelli Vigolo All Rights Reserved.
 *
 * Use of this source code is governed by a MIT-style license that can be
 * found in the LICENSE file at https://github.com/mauriciovigolo/keycloak-angular/blob/main/LICENSE.md
 */

import Keycloak, { KeycloakInitOptions, KeycloakServerConfig } from 'keycloak-js';
import {
  EnvironmentInjector,
  EnvironmentProviders,
  inject,
  isDevMode,
  makeEnvironmentProviders,
  provideAppInitializer,
  Provider,
  runInInjectionContext,
} from '@angular/core';
import {
  AutoRefreshTokenService,
  createKeycloakSignal,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  KEYCLOAK_EVENT_SIGNAL,
  ProvideKeycloakOptions,
  UserActivityService,
  withAutoRefreshToken,
} from 'keycloak-angular';
import { RealmService } from './services/realm.service';

/**
 * Provides Keycloak initialization logic for the app initializer phase.
 * Ensures Keycloak is initialized and features are configured.
 *
 * @param keycloak - The Keycloak instance.
 * @param options - ProvideKeycloakOptions for configuration.
 * @returns EnvironmentProviders or an empty array if `initOptions` is not provided.
 */
const provideKeycloakInAppInitializer = (
  keycloak: Keycloak,
  options: ProvideKeycloakOptions,
): EnvironmentProviders | Provider[] => {
  const { initOptions, features = [] } = options;

  if (!initOptions) {
    return [];
  }

  return provideAppInitializer(async () => {
    const injector = inject(EnvironmentInjector);
    runInInjectionContext(injector, () => features.forEach((feature) => feature.configure()));
    await keycloak.init(initOptions).catch((_) => {
      injector.get(RealmService).clearStorage();
    });
  });
};

/**
 * Configures and provides Keycloak as a dependency in an Angular application.
 *
 * This function initializes a Keycloak instance with the provided configuration and
 * optional initialization options. It integrates Keycloak into Angular dependency
 * injection system, allowing easy consumption throughout the application. Additionally,
 * it supports custom providers and Keycloak Angular features.
 *
 * If `initOptions` is not provided, the Keycloak instance will not be automatically initialized.
 * In such cases, the application must call `keycloak.init()` explicitly.
 *
 * @param options - Configuration object for Keycloak:
 *   - `config`: The Keycloak configuration, including the server URL, realm, and client ID.
 *   - `initOptions` (Optional): Initialization options for the Keycloak instance.
 *   - `providers` (Optional): Additional Angular providers to include.
 *   - `features` (Optional): Keycloak Angular features to configure during initialization.
 *
 * @returns An `EnvironmentProviders` object integrating Keycloak setup and additional providers.
 *
 * @example
 * ```ts
 * import { provideKeycloak } from './keycloak.providers';
 * import { bootstrapApplication } from '@angular/platform-browser';
 * import { AppComponent } from './app/app.component';
 *
 * bootstrapApplication(AppComponent, {
 *   providers: [
 *     provideKeycloak({
 *       config: {
 *         url: 'https://auth-server.example.com',
 *         realm: 'my-realm',
 *         clientId: 'my-client',
 *       },
 *       initOptions: {
 *         onLoad: 'login-required',
 *       },
 *     }),
 *   ],
 * });
 * ```
 */
export function provideKeycloak(options: ProvideKeycloakOptions): EnvironmentProviders {
  const keycloak = new Keycloak(options.config);

  const providers = options.providers ?? [];
  const keycloakSignal = createKeycloakSignal(keycloak);

  return makeEnvironmentProviders([
    {
      provide: KEYCLOAK_EVENT_SIGNAL,
      useValue: keycloakSignal,
    },
    {
      provide: Keycloak,
      useValue: keycloak,
    },
    ...providers,
    provideKeycloakInAppInitializer(keycloak, options),
  ]);
}

export const provideKeycloakAngular = (config: KeycloakServerConfig): EnvironmentProviders => {
  let keycloakInitOptions: KeycloakInitOptions | undefined = undefined;
  if (config.realm && config.realm.length > 0) {
    keycloakInitOptions = {
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
      redirectUri: window.location.origin + '/',
      enableLogging: isDevMode(),
    };
  }

  return provideKeycloak({
    config: {
      url: config.url,
      realm: config.realm,
      clientId: config.clientId,
    },
    initOptions: keycloakInitOptions,
    features: [
      withAutoRefreshToken({
        onInactivityTimeout: 'logout',
        sessionTimeout: 2 * 60 * 60 * 1000, // 120 minutes
      }),
    ],
    providers: [
      AutoRefreshTokenService,
      UserActivityService,
      {
        provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
        useValue: [
          {
            /**
             * @todo [PRODEV-3551]
             * Replace this hardcoded URL pattern with a value
             * dynamically loaded from environment variables.
             *
             * This configuration should be enhanced to fetch the `urlPattern`
             * dynamically based on deployment-specific environment settings.
             *
             * Task: PRODEV-3551
             */
            urlPattern: /^.*$/,
          },
        ],
      },
    ],
  });
};
